import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";

import { IMPORT_GLOBAL_ASSET } from "../graphql/importGlobalAsset.mutation";

const log: any = LoggerModule?.getInstance();

export interface ImportGlobalAssetProps {
  costumeFeatureId: string;
  costumeTypeId: string;
}

export interface ImportGlobalAssetsProps {
  costumeFeatureIds: string[];
  costumeTypeId: string;
}

export interface IImportGlobalAssetMethods extends IPluginMethodMap {
  importGlobalAsset(args: ImportGlobalAssetProps): Promise<any>;
  importGlobalAssets(args: ImportGlobalAssetsProps): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getDisplayMessage = (err: any) => {
  const displayMessage =
    err?.graphQLErrors &&
    Array.isArray(err?.graphQLErrors) &&
    err?.graphQLErrors.find((item: any) => item.extensions);

  return (
    displayMessage?.extensions?.display_message ||
    err?.message ||
    "Something went wrong"
  );
};

export class ImportGlobalAssetService implements IAgentPlugin {
  readonly methods: IImportGlobalAssetMethods;

  constructor() {
    this.methods = {
      importGlobalAsset: this.importGlobalAsset.bind(this),
      importGlobalAssets: this.importGlobalAssets.bind(this),
    };
  }

  async importGlobalAsset(args: ImportGlobalAssetProps) {
    log?.blue?.("ImportGlobalAssetService.importGlobalAsset:", args);

    try {
      const graphqlModule = getGraphQLModule();
      const client = graphqlModule.getClient("graphql-gateway");

      if (!client) {
        throw new Error("GraphQL client not found");
      }

      if (!args?.costumeFeatureId) {
        throw new Error("Missing costume feature id");
      }

      if (!args?.costumeTypeId) {
        throw new Error("Missing costume type id");
      }

      const res = await client.mutate({
        mutation: IMPORT_GLOBAL_ASSET,
        variables: {
          costumeFeatureId: args.costumeFeatureId,
          costumeTypeId: args.costumeTypeId,
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.importGlobalAsset;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }

  async importGlobalAssets(args: ImportGlobalAssetsProps) {
    log?.blue?.("ImportGlobalAssetService.importGlobalAssets:", args);

    if (!Array.isArray(args?.costumeFeatureIds)) {
      throw new Error("Missing costume feature ids");
    }

    if (!args?.costumeTypeId) {
      throw new Error("Missing costume type id");
    }

    const importedAssets: any[] = [];

    for (const costumeFeatureId of args.costumeFeatureIds) {
      if (!costumeFeatureId) continue;

      const imported = await this.importGlobalAsset({
        costumeFeatureId,
        costumeTypeId: args.costumeTypeId,
      });

      if (imported) {
        importedAssets.push(imported);
      }
    }

    return {
      importedAssets,
      count: importedAssets.length,
    };
  }
}
